import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useAuth } from './AuthContext';
import { getUserSearches, getUserPreferences } from '../lib/firebaseService';
import { toast } from '../hooks/use-toast';

const AutoFetchContext = createContext({});

export const useAutoFetch = () => {
  const context = useContext(AutoFetchContext);
  if (!context) {
    throw new Error('useAutoFetch must be used within an AutoFetchProvider');
  }
  return context;
};

export const AutoFetchProvider = ({ children, onRefreshSearch }) => {
  const { user } = useAuth();
  const [activeIntervals, setActiveIntervals] = useState(new Map());
  const [userPreferences, setUserPreferences] = useState({ defaultFetchInterval: 60 });
  const intervalsRef = useRef(new Map());

  // Load user preferences on mount
  useEffect(() => {
    if (user) {
      loadUserPreferences();
    }
  }, [user]);

  const loadUserPreferences = async () => {
    if (!user?.uid) {
      // No authenticated user, use defaults
      setUserPreferences({ defaultFetchInterval: 60, enableNotifications: true });
      return;
    }

    try {
      const result = await getUserPreferences(user.uid);
      if (result.success) {
        setUserPreferences(result.preferences);
      }
    } catch (error) {
      console.error('Error loading user preferences:', error);
      // Fallback to defaults on error
      setUserPreferences({ defaultFetchInterval: 60, enableNotifications: true });
    }
  };

  // Load user preferences but don't setup intervals (server-side implementation planned)
  useEffect(() => {
    // Load preferences (handles both authenticated and non-authenticated states)
    loadUserPreferences();
  }, [user]);

  const setupAutoFetchForUserSearches = async () => {
    try {
      const result = await getUserSearches(user.uid);
      if (result.success) {
        const searchesWithAutoFetch = result.searches.filter(search => search.autoFetch);

        // Clear existing intervals
        clearAllIntervals();

        // Setup new intervals for auto-fetch enabled searches
        searchesWithAutoFetch.forEach(search => {
          setupSearchInterval(search.id, search.fetchInterval || userPreferences.defaultFetchInterval);
        });
      }
    } catch (error) {
      console.error('Error setting up auto-fetch:', error);
    }
  };

  const setupSearchInterval = (searchId, intervalMinutes) => {
    // Disabled: Server-side implementation planned
    console.log(`Auto-fetch toggle recorded for search ${searchId}: ${intervalMinutes} minutes (server-side implementation pending)`);
  };

  const removeSearchInterval = (searchId) => {
    // Disabled: Server-side implementation planned
    console.log(`Auto-fetch disabled for search ${searchId} (server-side implementation pending)`);
  };

  const clearAllIntervals = () => {
    intervalsRef.current.forEach((intervalId) => {
      clearInterval(intervalId);
    });
    intervalsRef.current.clear();
    setActiveIntervals(new Map());
  };

  const updateSearchAutoFetch = (searchId, enabled, intervalMinutes) => {
    // Only log the change - actual scheduling will be server-side
    if (enabled) {
      console.log(`Auto-fetch enabled for search ${searchId}: ${intervalMinutes || userPreferences.defaultFetchInterval} minutes`);
    } else {
      console.log(`Auto-fetch disabled for search ${searchId}`);
    }
  };

  const refreshAutoFetchSetup = () => {
    // No-op: Server-side implementation pending
    console.log('Auto-fetch setup refresh requested (server-side implementation pending)');
  };

  const getActiveIntervalCount = () => {
    return activeIntervals.size;
  };

  const getSearchInterval = (searchId) => {
    return activeIntervals.has(searchId);
  };

  const value = {
    activeIntervals,
    userPreferences,
    updateSearchAutoFetch,
    refreshAutoFetchSetup,
    getActiveIntervalCount,
    getSearchInterval,
    setupSearchInterval,
    removeSearchInterval
  };

  return (
    <AutoFetchContext.Provider value={value}>
      {children}
    </AutoFetchContext.Provider>
  );
};